/**
 * Fabric.js Textbox 글머리 기호 오버라이드
 *
 * _bullets 배열 (line 인덱스 기준, \n으로 구분된 각 줄에 1:1 대응):
 *   - { type: 'bullet', char: '◆' }  → 글머리 기호 + 들여쓰기
 *   - { type: 'number', numType, startAt } → 번호 + 들여쓰기
 *   - { type: 'indent' }              → 들여쓰기만 (Shift+Enter / soft break)
 *   - null                            → 기본 (들여쓰기 없음)
 */
import * as fabric from 'fabric';

const SCALED_BULLETS = new Set(['\u25C6', '\u25CF', '\u2756']);
const BULLET_SCALE = 0.8;

const BULLET_PRESETS = [
  { id: 'bullet',    label: '• 글머리 기호',    char: '\u2022' },
  { id: 'diamond',   label: '\u25C6 다이아몬드', char: '\u25C6' },
  { id: 'square',    label: '\u25A0 사각형',     char: '\u25A0' },
  { id: 'circle',    label: '\u25CF 원',         char: '\u25CF' },
  { id: 'diamond4',  label: '\u2756 큰다이아',   char: '\u2756' },
  { id: 'arrow',     label: '\u27A4 화살표',     char: '\u27A4' },
  { id: 'check',     label: '\u2713 체크마크',   char: '\u2713' },
  { id: 'checkBold', label: '\u2714 굵은체크',   char: '\u2714' },
];

const NUMBER_PRESETS = [
  { id: 'arabicPeriod',     label: '1. 2. 3.' },
  { id: 'arabicParenR',     label: '1) 2) 3)' },
  { id: 'romanUcPeriod',    label: 'I. II. III.' },
  { id: 'alphaUcPeriod',    label: 'A. B. C.' },
  { id: 'alphaLcPeriod',    label: 'a. b. c.' },
  { id: 'circleNumDbPlain', label: '\u2460 \u2461 \u2462' },
];

// ─── 번호 포맷팅 ───

const ROMAN = [[1000,'M'],[900,'CM'],[500,'D'],[400,'CD'],[100,'C'],[90,'XC'],[50,'L'],[40,'XL'],[10,'X'],[9,'IX'],[5,'V'],[4,'IV'],[1,'I']];
const toRoman = (n) => { let s=''; for (const [v,r] of ROMAN) { while (n>=v) { s+=r; n-=v; } } return s; };

const formatNumber = (num, numType) => {
  switch (numType) {
    case 'arabicPeriod': case 'ea1JpnKorPeriod': case 'ea1JpnKorPlain': return `${num}.`;
    case 'arabicParenR': return `${num})`;
    case 'romanUcPeriod': return `${toRoman(num)}.`;
    case 'romanLcPeriod': return `${toRoman(num).toLowerCase()}.`;
    case 'alphaUcPeriod': return `${String.fromCharCode(64 + ((num-1)%26) + 1)}.`;
    case 'alphaLcPeriod': return `${String.fromCharCode(96 + ((num-1)%26) + 1)}.`;
    case 'circleNumDbPlain': return num <= 20 ? String.fromCharCode(0x2460+num-1) : `${num}.`;
    default: return `${num}.`;
  }
};

// ─── 유틸리티 ───

const getBulletIndent = (textbox) => Math.round(textbox.fontSize * 1.2);
const getParagraphIndex = (text, pos) => text.substring(0, pos).split('\n').length - 1;

/** _styleMap에서 wrapped lineIndex → paragraph 정보 */
const getLineParaInfo = (textbox, lineIndex) => {
  const map = textbox._styleMap;
  if (!map || !map[lineIndex]) return { paraIdx: 0, isFirst: true };
  const paraIdx = map[lineIndex].line;
  return { paraIdx, isFirst: lineIndex === 0 || map[lineIndex - 1]?.line !== paraIdx };
};

/** bullet/number 렌더링 텍스트 반환 (indent/null → null) */
const getBulletText = (bullets, paraIdx) => {
  const b = bullets?.[paraIdx];
  if (!b) return null;
  if (b.type === 'bullet') return b.char || '\u2022';
  if (b.type === 'number') {
    let num = b.startAt || 1;
    for (let i = 0; i < paraIdx; i++) if (bullets[i]?.type === 'number') num++;
    return formatNumber(num, b.numType || 'arabicPeriod');
  }
  return null;
};

/** 해당 줄이 들여쓰기 대상인지 (bullet/number/indent 모두 해당) */
const hasIndent = (textbox, paraIdx) => {
  const b = textbox._bullets?.[paraIdx];
  return !!b && (b.type === 'bullet' || b.type === 'number' || b.type === 'indent');
};

const hasBullet = (textbox, paraIdx) => !!(textbox._bullets?.[paraIdx]);

/** _bullets가 모두 null이면 null로 정리 */
const cleanupBullets = (tb) => {
  if (!tb._bullets?.length || tb._bullets.every(b => b === null)) tb._bullets = null;
};

/** bullet 제거 후 렌더링 갱신 + 이벤트 차단 */
const removeBulletAndBlock = (tb, idx, e) => {
  tb._bullets[idx] = null;
  cleanupBullets(tb);
  tb.dirty = true;
  tb.initDimensions();
  tb.canvas?.requestRenderAll();
  e.preventDefault();
  e.stopImmediatePropagation();
};

// ─── _getLineLeftOffset: 들여쓰기 반영 ───
const origGetLineLeftOffset = fabric.Textbox.prototype._getLineLeftOffset;
fabric.Textbox.prototype._getLineLeftOffset = function (lineIndex) {
  const base = origGetLineLeftOffset.call(this, lineIndex);
  if (!this._bullets) return base;
  const { paraIdx } = getLineParaInfo(this, lineIndex);
  return hasIndent(this, paraIdx) ? base + getBulletIndent(this) : base;
};

// ─── _renderTextLine: 글머리 문자 그리기 ───
const origRenderTextLine = fabric.Textbox.prototype._renderTextLine;
fabric.Textbox.prototype._renderTextLine = function (method, ctx, line, left, top, lineIndex) {
  if (this._bullets) {
    const { paraIdx, isFirst } = getLineParaInfo(this, lineIndex);
    if (isFirst) {
      const bulletText = getBulletText(this._bullets, paraIdx);
      if (bulletText) {
        ctx.save();
        const s = this.getCompleteStyleDeclaration(lineIndex, 0);
        const fs = s.fontSize || this.fontSize;
        const bfs = SCALED_BULLETS.has(bulletText) ? fs * BULLET_SCALE : fs;
        ctx.font = `${s.fontStyle||this.fontStyle||'normal'} ${s.fontWeight||this.fontWeight||'normal'} ${bfs}px "${s.fontFamily||this.fontFamily}"`;
        ctx.fillStyle = s.fill || (typeof this.fill === 'string' ? this.fill : '#000000');
        const yFix = SCALED_BULLETS.has(bulletText) ? (1-BULLET_SCALE)*fs*0.5 : 0;
        ctx[method](bulletText, left - getBulletIndent(this), top - fs*0.28 - yFix);
        ctx.restore();
      }
    }
  }
  origRenderTextLine.call(this, method, ctx, line, left, top, lineIndex);
};

// ─── onKeyDown: Enter/Backspace/Delete 글머리 관리 ───
const origOnKeyDown = fabric.Textbox.prototype.onKeyDown;
fabric.Textbox.prototype.onKeyDown = function (e) {
  if (this.isEditing && this._bullets) {
    const pos = this.selectionStart;
    const hasSel = this.selectionStart !== this.selectionEnd;

    // 선택 영역 내 \n 제거 (Backspace/Delete/Enter 공통)
    if (hasSel && [8, 46, 13].includes(e.keyCode) && this._bullets) {
      const s = Math.min(this.selectionStart, this.selectionEnd);
      const ed = Math.max(this.selectionStart, this.selectionEnd);
      const nlCount = (this.text.substring(s, ed).match(/\n/g) || []).length;
      if (nlCount > 0) {
        this._bullets.splice(getParagraphIndex(this.text, s) + 1, nlCount);
        cleanupBullets(this);
      }
    }

    // Enter: 새 줄 + bullet/indent 삽입
    if (e.keyCode === 13 && this._bullets) {
      const ep = hasSel ? Math.min(this.selectionStart, this.selectionEnd) : pos;
      const txt = hasSel
        ? this.text.substring(0, Math.min(this.selectionStart, this.selectionEnd)) + this.text.substring(Math.max(this.selectionStart, this.selectionEnd))
        : this.text;
      const pi = txt.substring(0, ep).split('\n').length - 1;

      if (e.shiftKey) {
        this._bullets.splice(pi + 1, 0, { type: 'indent' });
      } else {
        let nb = null;
        for (let i = pi; i >= 0; i--) {
          const b = this._bullets[i];
          if (b && b.type !== 'indent') { nb = { ...b }; break; }
        }
        this._bullets.splice(pi + 1, 0, nb);
      }
    }

    // Backspace (선택 없음)
    if (e.keyCode === 8 && !e.ctrlKey && !e.metaKey && !hasSel && this._bullets) {
      if (pos === 0 && this._bullets[0]?.type !== 'indent' && this._bullets[0]) {
        return removeBulletAndBlock(this, 0, e);
      }
      if (pos > 0 && this.text[pos - 1] === '\n') {
        const pi = getParagraphIndex(this.text, pos);
        if (this._bullets[pi] && this._bullets[pi].type !== 'indent') {
          return removeBulletAndBlock(this, pi, e);
        }
        this._bullets.splice(pi, 1);
        cleanupBullets(this);
      }
    }

    // Delete (선택 없음)
    if (e.keyCode === 46 && !e.ctrlKey && !e.metaKey && !hasSel && this._bullets) {
      if (pos < this.text.length && this.text[pos] === '\n') {
        const npi = getParagraphIndex(this.text, pos + 1);
        if (this._bullets[npi] && this._bullets[npi].type !== 'indent') {
          return removeBulletAndBlock(this, npi, e);
        }
        this._bullets.splice(npi, 1);
        cleanupBullets(this);
      }
    }
  }
  return origOnKeyDown.call(this, e);
};

// ─── ActiveSelection: 큰 바운딩 박스 제거, 개별 선택 박스만 표시 ───

fabric.ActiveSelection.prototype._renderControls = function (ctx, styleOverride, childrenOverride) {
  ctx.save();
  ctx.globalAlpha = this.isMoving ? this.borderOpacityWhenMoving : 1;
  const options = {
    hasControls: false,
    ...childrenOverride,
    forActiveSelection: true,
  };
  for (let i = 0; i < this._objects.length; i++) {
    this._objects[i]._renderControls(ctx, options);
  }
  // super._renderControls 생략 → 큰 파란 바운딩 박스 없음
  ctx.restore();
};

export { BULLET_PRESETS, NUMBER_PRESETS, getBulletText, hasBullet, getParagraphIndex };
